import React from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import defaultLinks from '../../assets/images/defaultLinks.svg';
import {
  Assets,
  Badge,
  InformationCard,
  InformationItem,
  Typography,
  styledComponent,
} from '@dewise-devex/backstage-plugin-mui-uikit-react';
import {
  StringUtil,
  useComponentEntity,
} from '@dewise-devex/backstage-plugin-utilities-common-client';
import useAsync from 'react-use/esm/useAsync';
import { commonProductApiRef } from '../../plugin';
import { useApi } from '@backstage/core-plugin-api';

const LinkItem = styledComponent('div')(({ theme }) => ({
  backgroundColor: theme.palette.surface.outlineVariant,
  display: 'flex',
  padding: '8px 24px 8px 16px',
  alignItems: 'center',
  gap: '16px',
  flex: '1 0 0',
  alignSelf: 'stretch',
  color: theme.palette.primary.main,
  marginBottom: '8px',
  '&:hover': {
    textDecoration: 'underline',
    cursor: 'pointer',
  },
}));

export const Achievements = () => {
  const { entity } = useEntity();
  const productService = useApi(commonProductApiRef);
  const componentService = useComponentEntity();

  const { value = [], loading } = useAsync(async () => {
    const productComponents = await componentService.byOwner(entity);
    let productAchievements: InformationItem[] = [];

    if (productComponents) {
      for (const component of productComponents) {
        const achievements: Record<string, any>[] =
          await productService.getAchievements(component);
        productAchievements = productAchievements.concat(
          formatProductAchievements(achievements),
        );
      }
    }

    return productAchievements;
  }, [entity]);

  const gotoLinks = (url: string) => {
    window.open(url);
  };

  return (
    <InformationCard as="custom">
      {!loading && (
        <div style={{ marginBottom: '16px' }}>
          <Typography style={{ marginBottom: '18px' }} variant="titleMD">
            Achievements
          </Typography>
          {value.length > 0 ? (
            value.map(item => (
              <div key={item.id}>
                <Badge
                  name={item.primary}
                  img={item.image}
                  description={item.secondary ?? ''}
                  dateAwarded={item.metadata?.dateAwarded ?? ''}
                  version={item.metadata?.version ?? ''}
                />
              </div>
            ))
          ) : (
            <div>No Badges</div>
          )}
        </div>
      )}

      <div style={{ marginBottom: '16px' }}>
        <Typography style={{ marginBottom: '18px' }} variant="titleMD">
          About
        </Typography>
        <Typography variant="bodyMD">{entity.metadata.description}</Typography>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <Typography style={{ marginBottom: '18px' }} variant="titleMD">
          Tags
        </Typography>
        {entity.metadata.tags && entity.metadata.tags.length > 0 ? (
          entity.metadata.tags.map(tag => (
            <Typography key={tag} variant="labelSM">
              {tag}
            </Typography>
          ))
        ) : (
          <div>No Tags</div>
        )}
      </div>
      <div style={{ marginBottom: '16px' }}>
        <Typography style={{ marginBottom: '18px' }} variant="titleMD">
          Links
        </Typography>
        {entity.metadata.links && entity.metadata.links.length > 0 ? (
          entity.metadata.links.map(item => (
            <LinkItem key={item.url}>
              <img
                src={StringUtil.toFaviconUrl(item.url) || defaultLinks}
                alt="icon"
                width={18}
                height={18}
                style={{ borderRadius: '5px' }}
              />
              <Typography
                key={item.title}
                variant="labelLG"
                onClick={() => gotoLinks(item.url)}
              >
                {item.title}
              </Typography>
            </LinkItem>
          ))
        ) : (
          <div>No Links</div>
        )}
      </div>
    </InformationCard>
  );
};

const badgeImages: { [key: string]: string } = {
  'Effective Collaboration': Assets.badgeEffectiveCollaboration,
  'Quality Code': Assets.badgeQualityCode,
  'Wise and Adaptive': Assets.badgeWiseAndAdaptive,
};

export function formatProductAchievements(
  achievements: Record<string, any>[],
): InformationItem[] {
  return achievements.map(achievement => ({
    id: achievement.id,
    primary: achievement.display_name,
    image: badgeImages[achievement.display_name] ?? undefined,
    secondary: achievement.description,
    metadata: {
      version: achievement.version,
      dateAwarded: achievement.start_date,
    },
  }));
}
